import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Row, Space, Tag, TimePicker, Typography, message, Spin,
} from 'antd';
import {
  ClockCircleOutlined,
  SaveOutlined,
  TeamOutlined,
  UndoOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AccountRepositoryImpl } from '../../Modules/Account/Data/repositories/AccountRepositoryImpl';
import { GetSlotOpsTimeSettingsUseCase } from '../../Modules/Account/Domain/UseCase/GetSlotOpsTimeSettingsUseCase';
import { UpdateSlotOpsTimeSettingsUseCase } from '../../Modules/Account/Domain/UseCase/UpdateSlotOpsTimeSettingsUseCase';
import {
  DailyTimeCutoff,
  LimitedAdminTimeSetting,
  SlotOpsTimeCutoff,
  SlotOpsTimeSettings,
} from '../../Modules/Account/Model/SlotOpsTimeSettings';

const { Title, Text, Paragraph } = Typography;

dayjs.extend(customParseFormat);

const toDayjs = (value?: string | null): Dayjs => {
  const parsed = dayjs(value || '17:00', 'HH:mm', true);
  return parsed.isValid() ? parsed : dayjs('17:00', 'HH:mm');
};

const toHHmm = (value: Dayjs | null, fallback: string): string =>
  value?.isValid() ? value.format('HH:mm') : fallback;

type DailyDraft = Dayjs[];
type LimitedAdminDraft = Record<string, DailyDraft>;

const getDailyCutoffs = (setting: SlotOpsTimeCutoff): DailyTimeCutoff[] => {
  if (setting.daily_cutoffs?.length === 7) {
    return [...setting.daily_cutoffs].sort((a, b) => a.day - b.day);
  }
  return ['월', '화', '수', '목', '금', '토', '일'].map((label, day) => {
    const cutoff = day < 5 ? setting.weekday_cutoff : setting.weekend_cutoff;
    const cutoffMinutes = day < 5
      ? setting.weekday_cutoff_minutes
      : setting.weekend_cutoff_minutes;
    return { day, label, cutoff, cutoff_minutes: cutoffMinutes };
  });
};

const buildDailyDraft = (setting: SlotOpsTimeCutoff): DailyDraft =>
  getDailyCutoffs(setting)
    .map((item) => toDayjs(item.cutoff));

const buildLimitedAdminDraft = (
  admins: LimitedAdminTimeSetting[],
): LimitedAdminDraft => Object.fromEntries(
  admins.map((admin) => [
    admin.username,
    buildDailyDraft(admin),
  ]),
);

const DayTimeGrid: React.FC<{
  values: DailyDraft;
  disabled?: boolean;
  onChange: (day: number, value: Dayjs | null) => void;
}> = ({ values, disabled = false, onChange }) => (
  <Row gutter={[12, 16]}>
    {['월', '화', '수', '목', '금', '토', '일'].map((label, day) => (
      <Col xs={12} sm={8} md={6} lg={day < 5 ? 3 : 4} key={label}>
        <Text type="secondary">{label}요일</Text>
        <div style={{ marginTop: 8 }}>
          <TimePicker
            value={values[day]}
            format="HH:mm"
            minuteStep={5}
            allowClear={false}
            size="large"
            disabled={disabled}
            style={{ width: '100%' }}
            onChange={(value) => onChange(day, value)}
          />
        </div>
      </Col>
    ))}
  </Row>
);

const SlotOpsTimeSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SlotOpsTimeSettings | null>(null);
  const [subDaily, setSubDaily] = useState<DailyDraft>(
    [0, 1, 2, 3, 4, 5, 6].map((day) => toDayjs(day < 5 ? '17:00' : '12:00')),
  );
  const [limitedDraft, setLimitedDraft] = useState<LimitedAdminDraft>({});
  const [initialSub, setInitialSub] = useState<SlotOpsTimeCutoff | null>(null);
  const [initialLimited, setInitialLimited] = useState<LimitedAdminTimeSetting[]>([]);

  const applySettings = useCallback((next: SlotOpsTimeSettings) => {
    setSettings(next);
    setSubDaily(buildDailyDraft(next.sub_accounts));
    setInitialSub(next.sub_accounts);
    setLimitedDraft(buildLimitedAdminDraft(next.limited_admins));
    setInitialLimited(next.limited_admins);
  }, []);

  useEffect(() => {
    const repo = new AccountRepositoryImpl();
    const uc = new GetSlotOpsTimeSettingsUseCase(repo);
    setLoading(true);
    uc.execute()
      .then(applySettings)
      .catch(() => {
        message.error('시간 제한 설정을 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  }, [applySettings]);

  const isSubDirty = useMemo(() => {
    if (!initialSub) return false;
    return getDailyCutoffs(initialSub).some(
      (item) => toHHmm(subDaily[item.day], item.cutoff) !== item.cutoff,
    );
  }, [initialSub, subDaily]);

  const isLimitedDirty = useMemo(() => {
    return initialLimited.some((admin) => {
      const draft = limitedDraft[admin.username];
      if (!draft) return false;
      return getDailyCutoffs(admin).some(
        (item) => toHHmm(draft[item.day], item.cutoff) !== item.cutoff,
      );
    });
  }, [initialLimited, limitedDraft]);

  const canEditLimited = Boolean(settings?.can_edit_limited_admin);
  const isDirty = isSubDirty || (canEditLimited && isLimitedDirty);

  const handleReset = useCallback(() => {
    if (initialSub) {
      setSubDaily(buildDailyDraft(initialSub));
    }
    setLimitedDraft(buildLimitedAdminDraft(initialLimited));
    message.info('변경사항을 초기화했습니다.');
  }, [initialSub, initialLimited]);

  const updateLimitedDraft = useCallback((
    username: string,
    day: number,
    value: Dayjs | null,
    fallback: string,
  ) => {
    setLimitedDraft((previous) => ({
      ...previous,
      [username]: previous[username].map(
        (current, index) => (index === day ? value ?? toDayjs(fallback) : current),
      ),
    }));
  }, []);

  const updateSubDaily = useCallback((day: number, value: Dayjs | null) => {
    setSubDaily((previous) => previous.map(
      (current, index) => (index === day ? value ?? current : current),
    ));
  }, []);

  const handleSave = useCallback(async () => {
    if (saving || !settings) return;
    setSaving(true);
    try {
      const repo = new AccountRepositoryImpl();
      const uc = new UpdateSlotOpsTimeSettingsUseCase(repo);
      const next = await uc.execute({
        sub_accounts: {
          daily_cutoffs: getDailyCutoffs(settings.sub_accounts).map((item) => ({
            day: item.day,
            cutoff: toHHmm(subDaily[item.day], item.cutoff),
          })),
        },
        ...(canEditLimited && settings.limited_admins.length > 0
          ? {
              limited_admins: settings.limited_admins.map((admin) => ({
                username: admin.username,
                daily_cutoffs: getDailyCutoffs(admin).map((item) => ({
                  day: item.day,
                  cutoff: toHHmm(
                    limitedDraft[admin.username]?.[item.day] ?? null,
                    item.cutoff,
                  ),
                })),
              })),
            }
          : {}),
      });
      applySettings(next);
      message.success('시간 제한 설정이 저장되었습니다. 서버와 모든 프론트에 공통 적용됩니다.');
    } catch (error: any) {
      const detail = error?.response?.data?.error || error?.message;
      message.error(detail || '시간 제한 설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [
    applySettings,
    canEditLimited,
    limitedDraft,
    saving,
    settings,
    subDaily,
  ]);

  if (loading) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <Spin size="large" tip="설정을 불러오는 중..." />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: 1100, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Title level={2} style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClockCircleOutlined /> 슬롯 작업 시간 제한
            </Title>
            <Paragraph type="secondary" style={{ fontSize: 16, marginBottom: 0 }}>
              설정한 시각부터 슬롯 생성·수정·삭제·연장·환불이 제한됩니다. 한국 시간(KST) 기준입니다.
            </Paragraph>
          </div>
          <Space>
            <Button
              size="large"
              icon={<UndoOutlined />}
              onClick={handleReset}
              disabled={!isDirty || saving}
            >
              되돌리기
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty}
            >
              저장하기
            </Button>
          </Space>
        </div>

        <Alert
          type="info"
          showIcon
          message="적용 범위"
          description="이 설정은 스티젠과 리프트에 동일하게 적용됩니다."
        />

        <Card
          title={(
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              <TeamOutlined style={{ marginRight: 8 }} />
              하위 계정 시간 제한
            </span>
          )}
          style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        >
          <Paragraph type="secondary">
            총판/대행사/대대행사의 슬롯 작업 마감 시각을 요일별로 설정합니다. 해당 시각부터 제한됩니다.
          </Paragraph>
          <DayTimeGrid values={subDaily} onChange={updateSubDaily} />
        </Card>

        {settings?.limited_admins.length ? (
          <Card
            title={(
              <span style={{ fontSize: 18, fontWeight: 600 }}>
                <UserSwitchOutlined style={{ marginRight: 8 }} />
                제한형 관리자 시간 제한
              </span>
            )}
            style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
          >
            <Paragraph type="secondary">
              각 제한형 관리자의 시간을 개별 설정합니다.
              {canEditLimited
                ? ' uptoyou 계정에서만 수정할 수 있습니다.'
                : ' 현재는 조회만 가능하며, 수정은 uptoyou 계정에서만 할 수 있습니다.'}
            </Paragraph>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {settings.limited_admins.map((admin) => {
                const draft = limitedDraft[admin.username];
                return (
                  <Card
                    key={admin.username}
                    type="inner"
                    title={admin.display_username}
                    extra={(
                      <Tag color={admin.service === 'lift' ? 'purple' : 'blue'}>
                        {admin.impact_label}
                      </Tag>
                    )}
                  >
                    <DayTimeGrid
                      values={draft ?? buildDailyDraft(admin)}
                      disabled={!canEditLimited}
                      onChange={(day, value) => updateLimitedDraft(
                        admin.username,
                        day,
                        value,
                        getDailyCutoffs(admin)[day].cutoff,
                      )}
                    />
                  </Card>
                );
              })}
            </Space>
          </Card>
        ) : null}
      </Space>
    </div>
  );
};

export default SlotOpsTimeSettingsPage;
